import { AuthApiResponse } from './AuthApiSchema';

/**
 * Exhaustiveness guard for the switches below: adding a variant to a state or
 * DTO union makes the corresponding default case fail to compile — the value
 * no longer narrows to `never` — instead of silently falling through.
 * @param value The value that every case should have narrowed away.
 */
function assertNever(value: never): never {
    throw new Error(`Unexpected value: ${JSON.stringify(value)}`);
}

/**
 * Whether the current session is authenticated.
 *
 * Sourced from GET /session, which is cheap enough to poll. This is the only
 * state the application at large needs. The source's session state
 * (the sign-in flow) was never ported — signing in happens on the Next.js site.
 */
export type AuthState =
    | { readonly status: 'initializing' }
    | { readonly status: 'anonymous' }
    | {
          readonly status: 'authenticated';

          /**
           * The id of the effective user: the signed-in user, or the impersonated
           * user while an impersonation is active.
           */
          readonly userId: string;
      };

/** Sign-out is the only action here; the sign-in actions were never ported. */
export type ActionName = 'signOut';

/**
 * Whether an action is running, and whether the last one failed.
 *
 * Actions run one at a time, so at most one error can exist.
 */
export interface ActionState {
    readonly pendingAction: ActionName | null;
    readonly actionError: {
        readonly action: ActionName;
        readonly error: unknown;
    } | null;
}

/**
 * What an action resolves with. Actions never reject for request failures —
 * the error lands here — so calling one from an event handler cannot leave an
 * unhandled rejection behind.
 */
export interface ActionResult {
    /** Null when the action succeeded. */
    readonly error: unknown;
}

/**
 * Shared instances for the states that carry no data.
 *
 * A state snapshot may only be a new object when it actually changed, so
 * subscribers can compare snapshots by reference. The sharing is what
 * guarantees that; the freezing only keeps a consumer from writing to a
 * snapshot.
 */
export const initializingAuthState: AuthState = Object.freeze({
    status: 'initializing',
});

export const anonymousAuthState: AuthState = Object.freeze({
    status: 'anonymous',
});

export const idleActionState: ActionState = Object.freeze({
    pendingAction: null,
    actionError: null,
});

/**
 * Maps the session info to an auth state. Signing in happens on the Next.js
 * site, so a session still waiting for verification counts as anonymous here.
 * @param dto The session info.
 * @returns The auth state.
 */
export function toAuthState(dto: AuthApiResponse<'GetSessionInfoEndpoint'>): AuthState {
    switch (dto.status) {
        case 'Uninitialized':
        case 'PendingVerification':
            return anonymousAuthState;

        case 'Authenticated':
            return { status: 'authenticated', userId: dto.userId };

        default:
            return assertNever(dto);
    }
}

/**
 * Checks whether two auth states are equivalent.
 *
 * Adding a variant to AuthState makes this fail to compile rather than silently
 * comparing only the status. Adding a *field* to an existing variant does not —
 * extend the comparison by hand, or a change to it stops reaching subscribers.
 * @param a The first state.
 * @param b The second state.
 * @returns True when the states are equivalent.
 */
export function isSameAuthState(a: AuthState, b: AuthState): boolean {
    if (a.status !== b.status) {
        return false;
    }

    switch (a.status) {
        case 'initializing':
        case 'anonymous':
            return true;

        case 'authenticated':
            return b.status === 'authenticated' && a.userId === b.userId;

        default:
            return assertNever(a);
    }
}
